import _ from 'lodash';
import { parse, format, isValid, isAfter, parseISO } from 'date-fns';
import { Validators } from './components/form-builder/types';
import { CURRENCY_INPUT_DATE_VALUE, ISO_DATE_FORMAT } from '@constants/dates';
import { isValidUsZip } from '@lib/validators';
import { GetInsurancePolicyOverridesQuery } from '@generated-gql/generated-types';

enum ReviewInformationFormFields {
  gender = 'gender',
  birthdate = 'birthdate',
  creditScore = 'creditScore',
  zipCode = 'zipCode',
  carMake = 'carMake',
  carYear = 'carYear',
  carModel = 'carModel',
  yearBuilt = 'yearBuilt',
}

export interface ReviewInformationFormData {
  gender: 'MALE' | 'FEMALE' | 'NONBINARY';
  birthdate: string;
  creditScore: string;
  zipCode: string;
  carMake: string;
  carYear: string;
  carModel: string;
  yearBuilt: string;
}

export const validators: Validators<ReviewInformationFormData> = {
  birthdate: (value) => {
    const date = parse(value ?? '', CURRENCY_INPUT_DATE_VALUE, new Date());

    if (!isValid(date)) {
      return 'Please enter your birth date.';
    }
    if (isAfter(date, new Date())) {
      return 'Please enter a date in the past.';
    }

    return null;
  },
  gender: (value: string) => {
    if (!value) {
      return 'Please pick a value from the list.';
    }

    return null;
  },
  creditScore: (value: string) => {
    if (!value) {
      return 'Please pick a value from the list.';
    }

    return null;
  },
  carYear: (year: string) => {
    const yearInt = parseInt(year, 10);
    if (!year || yearInt < 1981 || yearInt > new Date().getFullYear()) {
      return "Please enter your car's model year.";
    }

    return null;
  },
  carMake: (value: string) => {
    return value ? null : "Please enter your car's make.";
  },
  carModel: (value: string) => {
    return value ? null : 'Please enter a valid car model.';
  },
  yearBuilt: (value: number) => {
    if (value < 1776 || value > new Date().getFullYear()) {
      return 'Please enter a valid build year.';
    }
  },
  zipCode: (value: string) => {
    return isValidUsZip(value) ? null : 'Please enter a valid zip code.';
  },
};

export function parseBirthdate(value?: string) {
  if (!value) {
    return null;
  }

  return format(
    parse(value, CURRENCY_INPUT_DATE_VALUE, new Date()),
    ISO_DATE_FORMAT,
  );
}

// Some of the GraphQL overrides need to be parsed into the format expected by the input fields.
// For example, the credit score needs to be rounded to the nearest range value.
export function convertResponseToFormData(
  response: GetInsurancePolicyOverridesQuery,
): Partial<ReviewInformationFormData> {
  const overrides = {
    ...response?.insurancePolicyOverrides?.overrides?.global,
    ...response?.insurancePolicyOverrides?.overrides?.policy,
  };
  const tuCreditScore = response?.tuCreditReportIdentityProfile?.vantageScore3;

  function convertField(
    field: keyof ReviewInformationFormData,
    value: any,
  ): any {
    if (!value) {
      return value;
    }

    switch (field) {
      case 'creditScore':
        if (value <= 599) {
          return 550;
        }
        if (value >= 600 && value <= 649) {
          return 625;
        }
        if (value >= 650 && value <= 699) {
          return 675;
        }
        if (value >= 700 && value <= 749) {
          return 725;
        }
        return 825;
      case 'birthdate':
        return value
          ? format(parseISO(value), CURRENCY_INPUT_DATE_VALUE)
          : null;
      default:
        return value;
    }
  }

  return Object.keys(
    _.pick(overrides, Object.keys(ReviewInformationFormFields)),
  ).reduce((acc, key) => {
    if (key === ReviewInformationFormFields.creditScore && tuCreditScore) {
      acc[key] = tuCreditScore;
    } else {
      acc[key] = convertField(
        key as keyof ReviewInformationFormData,
        overrides[key],
      );
    }
    return acc;
  }, {});
}
